import type { LessonDetails } from "@/features/workspaces/types/lessonDetails";

import { differenceInMinutes, formatDuration, isAfter } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";

// Status IDs
const STATUS_IDS = {
  CREATED: "01F8MECHZX3TBDSZ7K9QDYFFJA",
  PAID: "01F8MECHZX3TBDSZ7K9QDYFFJB",
  IN_PROGRESS: "01F8MECHZX3TBDSZ7K9QDYFFJC",
  SESSION_FINISHED: "01F8MECHZX3TBDSZ7K9QDYFFJD",
  COMPLETED: "01F8MECHZX3TBDSZ7K9QDYFFJE",
};

export const getCardStyles = (statusId: string) => {
  switch (statusId) {
    case "01F8MECHZX3TBDSZ7K9QDYFFJE": // Completed
      return {
        border: "border-l-muted-foreground/30",
        badge: "bg-muted text-muted-foreground",
        text: "text-muted-foreground",
        card: "bg-muted/30",
      };
    case "01F8MECHZX3TBDSZ7K9QDYFFJD": // Session Finished
      return {
        border: "border-l-muted-foreground/40",
        badge: "bg-muted text-muted-foreground",
        text: "text-accent-foreground",
        card: "bg-muted/40",
      };
    case "01F8MECHZX3TBDSZ7K9QDYFFJA": // Created
      return {
        border: "border-l-blue-300/75",
        badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        text: "text-accent-foreground",
        card: "bg-blue-500/5",
      };
    case "01F8MECHZX3TBDSZ7K9QDYFFJB": // Paid
      return {
        border: "border-l-yellow-300/75",
        badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        text: "text-accent-foreground",
        card: "bg-yellow-500/5",
      };
    case "01F8MECHZX3TBDSZ7K9QDYFFJC": // In Progress
      return {
        border: "border-l-green-500/75",
        badge: "bg-green-500/20 text-green-400 border-green-500/30",
        text: "text-accent-foreground",
        card: "bg-green-500/5",
      };
    default:
      return {
        border: "border-l-border",
        badge: "bg-muted text-muted-foreground",
        text: "text-accent-foreground",
        card: "bg-foreground/50",
      };
  }
};

export const getStatusBadgeText = (statusId: string, t: any, statusName: string) => {
  switch (statusId) {
    case "01F8MECHZX3TBDSZ7K9QDYFFJA":
      return t("workspaces:status.created", "Created");
    case "01F8MECHZX3TBDSZ7K9QDYFFJB":
      return t("workspaces:status.paid", "Paid");
    case "01F8MECHZX3TBDSZ7K9QDYFFJC":
      return t("workspaces:status.inProgress", "In Progress");
    case "01F8MECHZX3TBDSZ7K9QDYFFJD":
      return t("workspaces:status.sessionFinished", "Session Finished");
    case "01F8MECHZX3TBDSZ7K9QDYFFJE":
      return t("workspaces:status.completed", "Completed");
    default:
      return statusName;
  }
};

/**
 * Check if a lesson can be cancelled
 * (not in the past and not already completed)
 */
export const canCancelLesson = (
  lessonDetails: LessonDetails | null,
  timezone: string | null = null,
): boolean => {
  if (!lessonDetails) return false;

  const now = toZonedTime(new Date(), timezone ?? "UTC");
  const lessonStart = new Date(lessonDetails.start_time.replace(" ", "T"));
  const statusId = lessonDetails.status.id;

  // Can cancel if lesson is in the future and not completed
  return (
    isAfter(lessonStart, now) &&
    statusId !== STATUS_IDS.SESSION_FINISHED &&
    statusId !== STATUS_IDS.COMPLETED
  );
};

/**
 * Check if a lesson can be joined
 * (within 15 minutes of start time, not ended yet)
 */
export const canJoinLesson = (
  lessonDetails: LessonDetails | null,
  timezone: string | null = null,
): boolean => {
  if (!lessonDetails) return false;

  const now = toZonedTime(new Date(), timezone ?? "UTC");
  const lessonStart = new Date(lessonDetails.start_time.replace(" ", "T"));
  const lessonEnd = new Date(lessonDetails.end_time.replace(" ", "T"));
  const statusId = lessonDetails.status.id;

  // Can join if lesson is in progress or about to start (within 15 minutes)
  const fifteenMinutesBefore = new Date(lessonStart.getTime() - 15 * 60000);

  return (
    isAfter(now, fifteenMinutesBefore) &&
    isAfter(lessonEnd, now) &&
    statusId !== STATUS_IDS.COMPLETED
  );
};

export const getTimeUntilLesson = (
  lessonDetails: LessonDetails | null | Date,
  locale: string,
  timezone: string | null | undefined,
): string | null => {
  if (!lessonDetails) return null;

  let lessonStart: Date;

  if ("start_time" in lessonDetails) {
    lessonStart = new Date(lessonDetails.start_time.replace(" ", "T"));
  } else {
    lessonStart = lessonDetails;
  }

  const now = toZonedTime(new Date(), timezone ?? "UTC");

  if (!isAfter(lessonStart, now)) return null;

  // Calculate the difference in various time units
  const diffInMinutes = differenceInMinutes(lessonStart, now);

  // Calculate months (approximate)
  const months = Math.floor(diffInMinutes / (60 * 24 * 30));
  const remainingAfterMonths = diffInMinutes - months * 60 * 24 * 30;

  // Calculate weeks
  const weeks = Math.floor(remainingAfterMonths / (60 * 24 * 7));
  const remainingAfterWeeks = remainingAfterMonths - weeks * 60 * 24 * 7;

  // Calculate days
  const days = Math.floor(remainingAfterWeeks / (60 * 24));
  const remainingAfterDays = remainingAfterWeeks - days * 60 * 24;

  // Calculate hours and minutes
  const hours = Math.floor(remainingAfterDays / 60);
  const minutes = remainingAfterDays % 60;

  // Use formatDuration with the appropriate locale and include all relevant time units
  return formatDuration(
    {
      months: months > 0 ? months : undefined,
      weeks: weeks > 0 ? weeks : undefined,
      days: days > 0 ? days : undefined,
      hours: hours > 0 ? hours : undefined,
      minutes: minutes > 0 ? minutes : undefined,
    },
    { locale: getDateFnsLocale(locale) },
  );
};
